import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer
} from 'react';

export interface DirtyState {
  id: string;
  message?: string;
}

interface UnsavedChangesState {
  dirtyFlags: Map<string, DirtyState>;
}

type UnsavedChangesAction =
  | { type: 'REGISTER_DIRTY'; payload: DirtyState }
  | { type: 'UNREGISTER_DIRTY'; payload: { id: string } }
  | { type: 'CLEAR_ALL' };

interface UnsavedChangesContextValue {
  state: UnsavedChangesState;
  registerDirty: (id: string, message?: string) => void;
  unregisterDirty: (id: string) => void;
  clearAll: () => void;
  hasUnsavedChanges: boolean;
  getDirtyStates: () => DirtyState[];
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
  null
);

function unsavedChangesReducer(
  state: UnsavedChangesState,
  action: UnsavedChangesAction
): UnsavedChangesState {
  switch (action.type) {
    case 'REGISTER_DIRTY':
      return {
        ...state,
        dirtyFlags: new Map(state.dirtyFlags).set(
          action.payload.id,
          action.payload
        )
      };
    case 'UNREGISTER_DIRTY': {
      const newDirtyFlags = new Map(state.dirtyFlags);
      newDirtyFlags.delete(action.payload.id);
      return {
        ...state,
        dirtyFlags: newDirtyFlags
      };
    }
    case 'CLEAR_ALL':
      return {
        ...state,
        dirtyFlags: new Map()
      };
    default:
      return state;
  }
}

interface UnsavedChangesProviderProps {
  children: ReactNode;
}

export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({
  children
}) => {
  const [state, dispatch] = useReducer(unsavedChangesReducer, {
    dirtyFlags: new Map()
  });

  const registerDirty = useCallback((id: string, message?: string) => {
    dispatch({
      type: 'REGISTER_DIRTY',
      payload: { id, message }
    });
  }, []);

  const unregisterDirty = useCallback((id: string) => {
    dispatch({
      type: 'UNREGISTER_DIRTY',
      payload: { id }
    });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  const hasUnsavedChanges = state.dirtyFlags.size > 0;

  const getDirtyStates = useCallback((): DirtyState[] => {
    return Array.from(state.dirtyFlags.values());
  }, [state.dirtyFlags]);

  const value: UnsavedChangesContextValue = useMemo(
    () => ({
      state,
      registerDirty,
      unregisterDirty,
      clearAll,
      hasUnsavedChanges,
      getDirtyStates
    }),
    [
      state,
      registerDirty,
      unregisterDirty,
      clearAll,
      hasUnsavedChanges,
      getDirtyStates
    ]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChangesContext = (): UnsavedChangesContextValue => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      'useUnsavedChangesContext must be used within an UnsavedChangesProvider'
    );
  }
  return context;
};
